import { memo, useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useMediaQuery } from 'react-responsive';

import { useQuery } from '@lichtblick/apollo';
import { useSession } from '@lichtblick/auth';
import { useTranslation } from '@lichtblick/i18next';
import {
  GetWorkOrderFilesMediaPayloadModel,
  GetWorkOrderFilesPayloadModel,
  TreeNode,
  GetWorkOrder_V1Query,
  GetWorkOrder_V1QueryVariables,
  GetUserUserPayloadModel,
  SidesheetItem,
} from '@lichtblick/types';
import {
  createContent,
  findFolderById,
  useLogoutOnApolloError,
  BREAKPOINTS,
  downloadWorkOrderFolder,
} from '@lichtblick/utils';

import { FileContent } from './FileContent';
import { FileGridView } from './FileGridView';
import { findFirstFileFolder, FolderTree } from './FolderTree';
import { GET_ORDER } from './graphql/order';
import { useCurrentUser } from './hooks/useCurrentUser';
import { ActionButtons, BtnWrapper, MainContainer, TreeContainer } from './TreeView.styles';
import { UploadModal } from './UploadModal';
import { getDownloadActionLabel, shouldShowDownloadAction } from './utils';

import { Button } from '../Button';
import { FileFilterType, useFileFilters } from '../FileBrowser';
import { PanelComponents } from '../PanelLayout';
import { SegmentedButton } from '../SegmentedButton';
import { Sidesheet } from '../Sidesheet';
import { Text } from '../Text';

enum FolderViewMode {
  List = 'list',
  Grid = 'grid',
}

type WorkOrderData = GetWorkOrder_V1Query['getWorkOrder_V1']['data'];

type FolderTreeViewProps = {
  allowMultiSelect?: boolean;
  /** Pre-fetched current user. When provided, the internal useCurrentUser query is skipped. */
  currentUser?: GetUserUserPayloadModel;
  initialFolderId: number | undefined;
  /** Loading state for externally provided workOrderData. Only used when workOrderData prop is provided. */
  isLoadingWorkOrderData?: boolean;
  isMonday?: boolean;
  isShowingSideBar: boolean;
  orignalData: GetWorkOrderFilesPayloadModel[] | undefined | null;
  refetch: () => void;
  structure: TreeNode[];
  /** Pre-fetched work order data. When provided, the internal GET_ORDER query is skipped. */
  workOrderData?: WorkOrderData;
  workOrderId: number;
};

export const FolderTreeView = memo(
  ({
    allowMultiSelect = true,
    currentUser,
    initialFolderId,
    isLoadingWorkOrderData,
    isMonday,
    isShowingSideBar,
    orignalData,
    refetch,
    structure,
    workOrderData: workOrderDataProp,
    workOrderId,
  }: FolderTreeViewProps) => {
    const [currentFiles, setCurrentFiles] = useState<GetWorkOrderFilesMediaPayloadModel[]>([]);
    const [selectedFolder, setSelectedFolder] = useState<TreeNode | null>(null);
    const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
    const [viewMode, setViewMode] = useState<FolderViewMode>(FolderViewMode.List);
    const { data: session } = useSession();
    const { dispatchFileFilters, fileFilters } = useFileFilters();
    const folderRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
    const hasInitialized = useRef(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [content, setContent] = useState<SidesheetItem[] | undefined>();
    const [isDownloading, setIsDownloading] = useState(false);
    const { myself: fetchedMyself } = useCurrentUser();
    const myself = currentUser ?? fetchedMyself;

    const { t } = useTranslation();
    const skipWorkOrderQuery = workOrderDataProp !== undefined;
    const {
      data: workOrderQueryResult,
      error: workOrderError,
      loading: isLoadingWorkOrderQuery,
    } = useQuery<GetWorkOrder_V1Query, GetWorkOrder_V1QueryVariables>(GET_ORDER, {
      variables: { workOrderId },
      skip: skipWorkOrderQuery,
    });

    useLogoutOnApolloError(workOrderError);

    const resolvedWorkOrderData = skipWorkOrderQuery ? workOrderDataProp : workOrderQueryResult?.getWorkOrder_V1?.data;
    const isLoadingWorkOrder = skipWorkOrderQuery ? (isLoadingWorkOrderData ?? false) : isLoadingWorkOrderQuery;
    const rootFolders = structure.filter((item) => item.type === 'folder');

    const getFilesForFolderAndDescendants = useCallback(
      (folder: TreeNode): GetWorkOrderFilesMediaPayloadModel[] => {
        const filesByFolderId = new Map((orignalData ?? []).map((item) => [item.id, item.files ?? []]));
        const files: GetWorkOrderFilesMediaPayloadModel[] = [];

        const collectFiles = (node: TreeNode) => {
          files.push(...(filesByFolderId.get(node.id) ?? []));

          node.children?.forEach((child) => {
            if (child.type === 'folder') {
              collectFiles(child);
            }
          });
        };

        collectFiles(folder);

        return files;
      },
      [orignalData],
    );

    const handleFolderSelect = (folder: TreeNode) => {
      setSelectedFolder(folder);
      setSelectedFileIds([]);

      if (fileFilters?.folderId !== folder.id) {
        dispatchFileFilters({ type: FileFilterType.FOLDER, payload: folder.id });
      }

      const files = getFilesForFolderAndDescendants(folder);

      setCurrentFiles(files);
    };

    useEffect(() => {
      if (hasInitialized.current) {
        return;
      }

      if (initialFolderId && structure.length > 0) {
        const initialFolder = findFolderById(structure, initialFolderId);

        if (initialFolder) {
          setSelectedFolder(initialFolder);
          const files = getFilesForFolderAndDescendants(initialFolder);

          setCurrentFiles(files);

          if (fileFilters?.folderId !== initialFolder.id) {
            dispatchFileFilters({ type: FileFilterType.FOLDER, payload: initialFolder.id });
          }

          hasInitialized.current = true;
        }

        const folderElement = folderRefs.current.get(initialFolderId);

        folderElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      if (!initialFolderId && structure.length > 0 && !isMonday) {
        const firstFileFolder = findFirstFileFolder(rootFolders?.[0]);

        if (firstFileFolder) {
          handleFolderSelect(firstFileFolder);
          hasInitialized.current = true;
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFolderId, structure, orignalData, rootFolders]);

    useEffect(() => {
      if (!resolvedWorkOrderData) {
        return;
      }

      const currentContent = createContent(resolvedWorkOrderData, t);

      setContent(currentContent);
    }, [t, resolvedWorkOrderData]);

    useEffect(() => {
      if (selectedFolder) {
        const files = getFilesForFolderAndDescendants(selectedFolder);

        setCurrentFiles(files);
      }
    }, [getFilesForFolderAndDescendants, selectedFolder]);

    const isMobile = useMediaQuery({ maxWidth: BREAKPOINTS.SMALL });
    const totalWorkOrderFileCount = (orignalData ?? []).reduce(
      (count, folder) => count + (folder.files?.length ?? 0),
      0,
    );
    const isRootFolder = selectedFolder === null;
    const showDownloadAction = shouldShowDownloadAction({
      hasFiles: currentFiles.length > 0,
      totalFileCount: totalWorkOrderFileCount,
    });
    const headerDownloadLabel = t('pm:downloadFolder');
    const downloadLabel = t(
      getDownloadActionLabel({
        hasSelection: selectedFileIds.length > 0,
        isRootFolder,
        selectedFileCount: selectedFileIds.length,
        totalFileCount: totalWorkOrderFileCount,
      }),
    );

    useEffect(() => {
      if (isMobile) {
        setViewMode(FolderViewMode.Grid);
      }
    }, [isMobile]);

    const handleDownload = async () => {
      if (isDownloading) {
        return;
      }

      setIsDownloading(true);

      try {
        if (selectedFileIds.length > 0) {
          await downloadWorkOrderFolder(
            session,
            workOrderId,
            selectedFolder?.id,
            selectedFolder?.name,
            selectedFileIds,
          );

          return;
        }

        if (selectedFolder) {
          await downloadWorkOrderFolder(session, workOrderId, selectedFolder.id, selectedFolder.name);

          return;
        }

        if (isRootFolder) {
          const rootFolderId = rootFolders?.[0]?.id;

          if (rootFolderId) {
            await downloadWorkOrderFolder(session, workOrderId, rootFolderId, 'all-files');
          }
        }
      } finally {
        setIsDownloading(false);
      }
    };

    const mainContent = (
      <>
        <Text $variant={{ fontSize: '32px', fontWeight: '700' }}>{t('pm:documents')}</Text>
        <BtnWrapper>
          <SegmentedButton
            defaultValue={isMobile ? FolderViewMode.Grid.toString() : FolderViewMode.List.toString()}
            onOptionChange={(opt) => {
              if (opt === 'list') {
                setViewMode(FolderViewMode.List);
              }

              if (opt === 'grid') {
                setViewMode(FolderViewMode.Grid);
              }
            }}
            options={[
              { value: FolderViewMode.List, label: t('pm:list') },
              { value: FolderViewMode.Grid, label: t('pm:grid') },
            ]}
          />
          <ActionButtons>
            {showDownloadAction ? (
              <Button
                isLoading={isDownloading}
                label={isDownloading ? t('common:downloading') : headerDownloadLabel}
                onClick={() => void handleDownload()}
              />
            ) : null}
            {selectedFolder && (
              <UploadModal
                folderId={selectedFolder.id}
                isModalOpen={isModalOpen}
                onSuccess={() => {
                  refetch();
                }}
                setIsModalOpen={setIsModalOpen}
                workOrderId={workOrderId}
              />
            )}
          </ActionButtons>
        </BtnWrapper>
        {viewMode === FolderViewMode.List && (
          <FileContent
            allowMultiSelect={allowMultiSelect}
            data={currentFiles}
            downloadLabel={downloadLabel}
            folderId={selectedFolder?.id}
            isDownloading={isDownloading}
            isMonday={isMonday}
            onDownload={() => void handleDownload()}
            onSelectionChange={setSelectedFileIds}
            refetch={refetch}
            showDownloadAction={showDownloadAction}
            workOrderId={workOrderId}
          />
        )}
        {viewMode === FolderViewMode.Grid && (
          <FileGridView
            allowMultiSelect={allowMultiSelect}
            data={currentFiles}
            downloadLabel={downloadLabel}
            folderId={selectedFolder?.id}
            isDownloading={isDownloading}
            isMonday={isMonday}
            onDownload={() => void handleDownload()}
            onSelectionChange={setSelectedFileIds}
            refetch={refetch}
            showDownloadAction={showDownloadAction}
            workOrderId={workOrderId}
          />
        )}
      </>
    );

    const leftSidebarContent = (
      <TreeContainer>
        {rootFolders.map((item) => (
          <FolderTree
            data={item}
            folderRefs={folderRefs}
            key={item.id}
            onFolderSelect={handleFolderSelect}
            selectedFolder={selectedFolder}
          />
        ))}
      </TreeContainer>
    );

    let rightSidebarContent;

    if (isShowingSideBar) {
      if (isLoadingWorkOrder) {
        rightSidebarContent = <Skeleton height={'100vh'} width={'20rem'} />;
      } else if (content) {
        rightSidebarContent = <Sidesheet content={content} user={myself} />;
      } else {
        rightSidebarContent = <></>;
      }
    }

    return (
      <>
        <PanelComponents.LeftSidebar>{leftSidebarContent}</PanelComponents.LeftSidebar>
        {isShowingSideBar && <PanelComponents.RightSidebar>{rightSidebarContent}</PanelComponents.RightSidebar>}
        <MainContainer>{mainContent}</MainContainer>
      </>
    );
  },
);
